/** Human-readable label for a newsletter key/slug when no CMS-provided title is available. */
export function formatNewsletterKeyForDisplay(key: string): string {
    const trimmed = key.trim();
    if (!trimmed) {
        return '';
    }
    return trimmed
        .split(/[-_/]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

const NEWSLETTER_ENTRY_SELECTOR = '.newsletter-entry';
const DISPLAY_NAME_DATA_ATTR = 'data-display-name';

/**
 * Display name for a newsletter checkbox (Sitecore card title, harness label, or slug fallback).
 */
export function getNewsletterDisplayNameFromCheckbox(checkbox: HTMLInputElement): string {
    const entry = checkbox.closest(NEWSLETTER_ENTRY_SELECTOR);
    const fromHeading = entry?.querySelector('h3')?.textContent?.trim();
    if (fromHeading) {
        return fromHeading;
    }

    const fromAttr = checkbox.getAttribute(DISPLAY_NAME_DATA_ATTR)?.trim();
    if (fromAttr) {
        return fromAttr;
    }

    const label = checkbox.closest('label');
    if (label) {
        const clone = label.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('input').forEach((el) => el.remove());
        const labelText = clone.textContent?.trim();
        if (labelText) {
            return labelText;
        }
    }

    return formatNewsletterKeyForDisplay(String(checkbox.value ?? ''));
}

export interface NewsletterCheckboxSelection {
    id: string;
    displayName: string;
}

/** Checked `name="newsletter"` inputs: campaign id/slug for API, display name from slot markup. */
export function collectCheckedNewsletterSelections(form: HTMLFormElement): NewsletterCheckboxSelection[] {
    const selections: NewsletterCheckboxSelection[] = [];

    form.querySelectorAll<HTMLInputElement>('input[name="newsletter"]:checked').forEach((checkbox) => {
        const id = String(checkbox.value ?? '').trim();
        if (!id) {
            return;
        }
        selections.push({
            id,
            displayName: getNewsletterDisplayNameFromCheckbox(checkbox)
        });
    });

    return selections;
}

/**
 * Disabled / placeholder list IDs from CMS (legacy numeric -1, Beehiiv migration sentinels).
 * Non-numeric Beehiiv publication IDs remain enabled.
 */
export function isNewsletterListIdDisabled(listId: string): boolean {
    const s = listId?.trim() ?? '';
    if (!s) {
        return true;
    }
    const lower = s.toLowerCase();
    if (lower === '-1' || lower === '0' || lower === 'disabled' || lower === 'none') {
        return true;
    }
    const n = Number(s);
    if (!Number.isNaN(n) && n <= 0) {
        return true;
    }
    return false;
}
